import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutomobileRiskModels } from '../../../../models/automobile-risk.models';
import { HealthModels } from '../../../../models/health.models';
import { PaymentModels } from '../../../../models/payment.models';
import { QuotationModels } from '../../../../models/quotation.models';
import { TableModels } from '../../../../models/table.models';
import { DateProjector } from '../projectors/date-projector';
import { projectPaymentPayload } from '../projectors/payment-projectors';
import {
  projectAnonymousQuotationSubscriptionForm,
  projectAutomobileQuotationRequest,
  projectCarPricingResponse,
  projectCarPricingWithMetadataResponse,
  projectCreateQuotationRequest,
  projectQuotationPaymentCredentials,
  projectQuotationSummary,
  projectQuotationTableRow,
} from '../projectors/quotation-projector';
import { projectPaginatedResult } from '../projectors/table-projectors';
import { QuotationApiService } from './api/quotation-api.service';
import { CarInsuranceService } from './car-insurance.service';

@Injectable({
  providedIn: 'root',
})
export class QuotationService {
  constructor(private quotationApiService: QuotationApiService) {}

  getCarPricing(
    riskForm: AutomobileRiskModels.Form,
    formula: QuotationModels.Formula,
    brokerPricingData: QuotationModels.BrokerPricingData,
    details?: QuotationModels.FormulaDetails,
    quotationId?: string,
  ): Observable<QuotationModels.FullFormulaPricingInformations> {
    return this.quotationApiService
      .getCarPricing(
        projectAutomobileQuotationRequest(
          riskForm,
          quotationId,
          brokerPricingData.currentPackageId,
          formula.type,
          brokerPricingData.currentCommission,
          brokerPricingData.currentManagement,
          details,
        ),
      )
      .pipe(
        map((pricingResponse) => projectCarPricingResponse(pricingResponse)),
      );
  }

  generateQuotation(
    riskForm: CarInsuranceService,
    formula: QuotationModels.Formula,
    brokerPricingData: QuotationModels.BrokerPricingData,
    isModeB2C: boolean,
    sendEmail: boolean,
    details?: QuotationModels.FormulaDetails,
  ): Observable<string> {
    return this.quotationApiService.createQuotation(
      projectCreateQuotationRequest(
        riskForm,
        isModeB2C,
        sendEmail,
        brokerPricingData.currentPackageId,
        formula.type,
        brokerPricingData.currentCommission,
        brokerPricingData.currentManagement,
        details,
      ),
    );
  }

  getCarPricingWithMetadata(
    riskForm: CarInsuranceService,
  ): Observable<QuotationModels.BrokerPricingData> {
    return this.quotationApiService
      .getCarPricingWithMetadata(
        projectAutomobileQuotationRequest(
          riskForm.riskFormData,
          riskForm.quotationId!,
        ),
      )
      .pipe(
        map((apiResponse) =>
          projectCarPricingWithMetadataResponse(apiResponse),
        ),
      );
  }

  getQuotationSummary(
    quotationId: string,
    ignoreArchived: boolean,
  ): Observable<QuotationModels.QuotationSummary> {
    return this.quotationApiService
      .getQuotation(quotationId, ignoreArchived)
      .pipe(map((r) => projectQuotationSummary(r)));
  }
  getAnonymousQuotationSummary(
    credentials: QuotationModels.QuotationPaymentCredentials,
  ): Observable<QuotationModels.QuotationSummary> {
    return this.quotationApiService
      .getAnonymousQuotation(projectQuotationPaymentCredentials(credentials))
      .pipe(map((r) => projectQuotationSummary(r)));
  }

  checkPaymentCredentials(
    credentials: QuotationModels.QuotationPaymentCredentials,
  ): Observable<void> {
    return this.quotationApiService.getCanPay(
      projectQuotationPaymentCredentials(credentials),
    );
  }

  getAnonymousQuotationStatus(
    credentials: QuotationModels.QuotationPaymentCredentials,
  ): Observable<QuotationModels.QuotationStatusData> {
    return this.quotationApiService.getAnonymousQuotationStatus(
      projectQuotationPaymentCredentials(credentials),
    );
  }

  getQuotationStatus(
    quotationId: string,
  ): Observable<QuotationModels.QuotationStatusData> {
    return this.quotationApiService.getQuotationStatus(quotationId);
  }

  validateAnonymousSubscription(
    quotationId: string,
    birthDate: string,
    req: QuotationModels.SubscriptionForm,
    paymentReq: PaymentModels.PaymentRequest,
  ): Observable<PaymentModels.PayzenPayload> {
    return this.quotationApiService
      .validateAnonymousSubscription(
        quotationId,
        projectAnonymousQuotationSubscriptionForm(req, paymentReq, birthDate),
      )
      .pipe(map(projectPaymentPayload));
  }

  validateSubscription(
    quotationId: string,
    req: QuotationModels.SubscriptionForm,
    resil: HealthModels.Resilisation,
    paymentReq: PaymentModels.PaymentRequest,
  ): Observable<PaymentModels.PayzenPayload> {
    return this.quotationApiService
      .validateSubscription(quotationId, {
        dateEffet: DateProjector.fromAppToApi(req.infos.dateEffet)!,
        quittancement: req.quittancement!,
        quantieme: req.infos.quantieme!,
        payment: paymentReq,
        resiliation: resil,
      })
      .pipe(map(projectPaymentPayload));
  }

  getQuotationList(request: TableModels.PaginatedRequest) {
    return this.quotationApiService
      .getQuotationList(request)
      .pipe(map((q) => projectPaginatedResult(q, projectQuotationTableRow)));
  }

  archiveQuotation(quotationId: number) {
    return this.quotationApiService.archiveQuotation(quotationId);
  }
}
